import Header from "@components/Header/Header"
import { useNavigate, useLocation } from "react-router"
import SectionLabel from "@components/SectionLabel/SectionLabel";
import PrimaryButton from "@components/PrimaryButton/PrimaryButton";
import { useTranslation } from "react-i18next";
import {
    Container,
    ContentContainer,
    SectionContainer,
    ButtonContainer,
    DetailsCard,
    DetailsRow,
    Divider,
    InformationCard,
    BankInfoContainer,
    BankLogo,
    BankAccountName,
    BankAccountTag,
    BankAccountDetails,
    EditIconContainer,
    DisclaimerContainer,
    DisclaimerText
} from "./BankTransferPaymentConfirmation.styles";
import ConfirmationInformationRow from "@components/ConfirmationInformationRow/ConfirmationInformationRow";
import { toCurrencyFormat } from "@lib/utils";
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon";
import { useState } from "react";
import useOngoingTransaction from "@hooks/useOngoingTransaction"
import LoadingModal from "@components/LoadingModal/LoadingModal";
import TemporaryTokenMigrationGuard from "@components/TemporaryTokenMigration/TemporaryTokenMigrationGuard/TemporaryTokenMigrationGuard";

const BankTransferPaymentConfirmation = () => {
    const { data: ongoingTransactionInformation, isLoading } = useOngoingTransaction()
    const [showModal, setShowModal] = useState(false)

    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { paymentInformation } = location.state;
    const { paymentDestination, amount, memo, fee } = JSON.parse(paymentInformation);

    const handlePay = () => {
        if (!ongoingTransactionInformation?.canCreateNewTransaction) {
            setShowModal(true)
            return
        }
        navigate('/authentication', {
            state: {
                operationType: 'bank-transfer-payment',
                successRoute: '/bank-transfer-payment-receipt',
                operationData: {
                    ui_amount: amount,
                    destination_uuid: paymentDestination.uuid,
                    memo,
                    paymentDestination,
                    fee
                },
                useBiometrics: true
            }
        })
    }

    const handleBackClick = () => navigate('/bank-transfer-payment', { state: { paymentInformation } })

    return (
        <Container>
            <Header
                title={t('bankTransferPayment.confirmation.header')}
                showBackButton
                onBackClick={handleBackClick}
            />

            <LoadingModal visible={isLoading} />
            <TemporaryTokenMigrationGuard />

            {showModal && <div style={{ zIndex: 2000 }}>
                <ModalWithIcon
                    title={t('common.ongoingTransaction.title')}
                    description={t('common.ongoingTransaction.description')}
                    onPositive={handleBackClick}
                    positiveText={t('common.ongoingTransaction.positiveText')}
                    type="void"
                    visible={showModal}
                    onModalOverlayClick={() => setShowModal(false)}
                />
            </div>}

            <ContentContainer>
                <SectionContainer>
                    <SectionLabel>{t('bankTransferPayment.confirmation.destination')}</SectionLabel>
                    <PaymentInformationCard
                        iconUrl={paymentDestination.selectedBank.icon_url}
                        accountNumber={paymentDestination.accountNumber}
                        accountHolderName={paymentDestination.accountHolderName}
                        accountTag={paymentDestination.accountTag}
                        bank={paymentDestination.selectedBank.display_name}
                        onEdit={() => navigate('/bank-transfer-payment', { state: { paymentInformation } })}
                    />
                </SectionContainer>
                <SectionContainer>
                    <SectionLabel>{t('bankTransferPayment.confirmation.paymentDetails')}</SectionLabel>
                    <PaymentDetailsCard
                        amount={amount}
                        fee={fee}
                        memo={memo}
                    />
                </SectionContainer>
            </ContentContainer>
            <ButtonContainer>
                <PrimaryButton
                    onClick={handlePay}
                >
                    {t('bankTransferPayment.confirmation.payButton')}
                </PrimaryButton>
            </ButtonContainer>
        </Container>
    )
}

export default BankTransferPaymentConfirmation

const PaymentDetailsCard = ({
    amount,
    fee = 0,
    memo,
}: {
    amount: number;
    fee: number;
    memo: string;
}) => {
    const { t } = useTranslation();
    return (
        <DetailsCard>
            <DetailsRow>
                <ConfirmationInformationRow
                    title={t('bankTransferPayment.confirmation.recipientReceives')}
                    value={`Rp${toCurrencyFormat(amount)}`}
                />
            </DetailsRow>
            <DetailsRow>
                <ConfirmationInformationRow
                    title={t('bankTransferPayment.confirmation.fees')}
                    value={`Rp${toCurrencyFormat(fee)}`}
                />
            </DetailsRow>
            {memo && (
                <DetailsRow>
                    <ConfirmationInformationRow
                        title={t('bankTransferPayment.confirmation.memo')}
                        value={memo}
                    />
                </DetailsRow>
            )}

            <Divider />

            <DetailsRow>
                <ConfirmationInformationRow
                    title={t('bankTransferPayment.confirmation.totalAmount')}
                    value={`${toCurrencyFormat(amount + fee)} JIDR`}
                    boldValue
                />
            </DetailsRow>
        </DetailsCard>
    )
}

const PaymentInformationCard = ({
    iconUrl,
    accountNumber,
    accountHolderName,
    accountTag,
    bank,
    onEdit
}: {
    iconUrl: string,
    accountNumber: string,
    accountHolderName: string,
    accountTag: string,
    bank: string,
    onEdit?: () => void
}) => {
    const { t } = useTranslation();
    return (
        <InformationCard>
            <BankInfoContainer>
                <BankLogo
                    src={iconUrl}
                />
                <BankAccountName>
                    {accountHolderName}
                </BankAccountName>
                <BankAccountTag>
                    {accountTag}
                </BankAccountTag>
                <BankAccountDetails>
                    {bank} • {accountNumber}
                </BankAccountDetails>
            </BankInfoContainer>
            <EditIconContainer onClick={onEdit}>
                <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.75 2.75H3C2.17157 2.75 1.5 3.42158 1.5 4.25V12.5C1.5 13.3284 2.17157 14 3 14H11.25C12.0784 14 12.75 13.3284 12.75 12.5V8.75M11.6893 1.68934C12.2751 1.10355 13.2249 1.10355 13.8107 1.68934C14.3964 2.27513 14.3964 3.22487 13.8107 3.81066L7.37132 10.25H5.25L5.25 8.12868L11.6893 1.68934Z" stroke="#151515" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </EditIconContainer>
            <Divider />
            <DisclaimerContainer>
                <DisclaimerText>
                    {t('bankTransferPayment.confirmation.disclaimer')}
                </DisclaimerText>
            </DisclaimerContainer>
        </InformationCard>
    )
}